import { StateId, StateEntry, ContextConfig } from '../types';
import { NON_FOCUS_STATES } from '../config/states';
import {
  HEAD_SIDE_THRESH, HEAD_UP_THRESH, HEAD_DOWN_THRESH,
  HEAD_FAR_THRESH, RETURN_WINDOW, STATE_HISTORY_MAX,
} from '../config/constants';

export interface StateMachineInput {
  yaw: number;
  pitch: number;
  isSpeaking: boolean;
  continuousSpeechSec: number;
  keyboardZ: number;
  recentKeyIntervalMs: number;
  lastInputTime: number;
  isDrawing: boolean;
  hasFace: boolean;
  deviceIsFlat: boolean;
  config: ContextConfig;
}

const BURST_INTERVAL_MS = 120;
const SLOW_INTERVAL_MS = 800;

// Minimum time a state must persist before being committed to history (ms)
const STATE_DEBOUNCE_MS = 150;

export class StateMachine {
  currentState: StateId = 'FOCUSED';
  prevState: StateId = 'FOCUSED';
  stateChangedAt = 0;
  lastNonFocusState: StateId | null = null;
  lastNonFocusEnd = 0;
  history: StateEntry[] = [];

  // Debouncing: raw state vs committed state
  private rawState: StateId = 'FOCUSED';
  private rawStateStart = 0;

  determineState(input: StateMachineInput): StateId {
    const {
      yaw, pitch, isSpeaking, continuousSpeechSec, keyboardZ,
      recentKeyIntervalMs, lastInputTime, isDrawing, hasFace, deviceIsFlat, config,
    } = input;

    if (!hasFace) return 'IDLE';

    const now = Date.now();
    const absYaw = Math.abs(yaw);
    const combinedDist = Math.sqrt(yaw * yaw + pitch * pitch);
    const timeSinceInput = now - lastInputTime;

    const sideThresh = HEAD_SIDE_THRESH;
    const upThresh = deviceIsFlat ? HEAD_UP_THRESH * 2 : HEAD_UP_THRESH;
    const downThresh = deviceIsFlat ? HEAD_DOWN_THRESH * 2.5 : HEAD_DOWN_THRESH;
    const farThresh = deviceIsFlat ? HEAD_FAR_THRESH * 1.5 : HEAD_FAR_THRESH;

    // 1. Voice
    if (isSpeaking && continuousSpeechSec > 0.5) return 'LISTENING';

    // 2. Head direction (with hysteresis — require 2° more to EXIT than to ENTER)
    const hysteresis = this.isHeadState(this.currentState) ? 2 : 0;
    if (absYaw > sideThresh + hysteresis) return 'LOOKING_SIDE';
    if (pitch < -(downThresh + hysteresis)) return 'LOOKING_DOWN';
    if (pitch > upThresh + hysteresis) return 'THINKING_UP';
    if (combinedDist > farThresh + hysteresis) return 'GAZING_OUT';

    // If currently in a head state but within hysteresis band, stay in it
    if (this.isHeadState(this.currentState)) {
      if (this.currentState === 'LOOKING_SIDE' && absYaw > sideThresh) return 'LOOKING_SIDE';
      if (this.currentState === 'LOOKING_DOWN' && pitch < -downThresh) return 'LOOKING_DOWN';
      if (this.currentState === 'THINKING_UP' && pitch > upThresh) return 'THINKING_UP';
      if (this.currentState === 'GAZING_OUT' && combinedDist > farThresh) return 'GAZING_OUT';
    }

    const classifyTyping = (burstMultiplier: number = 1.0): StateId => {
      if (keyboardZ < -config.zThresh * burstMultiplier) return 'BURST_TYPING';
      if (keyboardZ > config.zThresh) return 'SLOW_TYPING';
      if (recentKeyIntervalMs > 0 && recentKeyIntervalMs < BURST_INTERVAL_MS) return 'BURST_TYPING';
      if (recentKeyIntervalMs > SLOW_INTERVAL_MS) return 'SLOW_TYPING';
      return 'WRITING_NORMAL';
    };

    // 3. Returned from non-focus?
    if (this.lastNonFocusState && (now - this.lastNonFocusEnd) < RETURN_WINDOW) {
      if (timeSinceInput < 1500 && recentKeyIntervalMs > 0) return classifyTyping(0.6);
      if (timeSinceInput < 1500) return 'WRITING_NORMAL';
      return 'RETURNED';
    }

    // 4. Typing state
    if (timeSinceInput < 2000 && recentKeyIntervalMs > 0) return classifyTyping();
    if (timeSinceInput < 2000) return 'WRITING_NORMAL';

    // 5. Stylus active
    if (isDrawing) return 'WRITING_NORMAL';

    // 6. Default
    return 'FOCUSED';
  }

  /**
   * Debounced transition: only commits to history if raw state persists for STATE_DEBOUNCE_MS.
   * Returns true if a new state was committed.
   */
  transition(newState: StateId): boolean {
    const now = Date.now();

    // Track raw state changes for debouncing
    if (newState !== this.rawState) {
      this.rawState = newState;
      this.rawStateStart = now;
    }

    // Don't commit until debounce period has passed
    if (newState !== this.currentState && (now - this.rawStateStart) < STATE_DEBOUNCE_MS) {
      return false;
    }

    if (newState === this.currentState) return false;

    // Track non-focus exit
    if (NON_FOCUS_STATES.has(this.currentState) && !NON_FOCUS_STATES.has(newState)) {
      this.lastNonFocusState = this.currentState;
      this.lastNonFocusEnd = now;
    }

    this.prevState = this.currentState;
    this.currentState = newState;
    this.stateChangedAt = now;

    this.history.push({ state: newState, t: now });
    if (this.history.length > STATE_HISTORY_MAX) this.history.shift();

    return true;
  }

  private isHeadState(state: StateId): boolean {
    return state === 'LOOKING_SIDE' || state === 'LOOKING_DOWN' ||
           state === 'THINKING_UP' || state === 'GAZING_OUT';
  }
}
